import React from 'react'
import classNames from 'classnames'
import { StarRating } from '@deal/components'
import { ReviewSummaryBucketsFragment } from '#src/generated/graphql'
import { scroller } from 'react-scroll'
import sortBy from 'lodash/sortBy'
import Star from './star.svg'
import styles from './styles.css'

interface Props {
  reviewSummaryBuckets?: ReviewSummaryBucketsFragment
  currentRatingFilter?: string
  onSetRatingFilter: (rating?: string) => void
}

const Summary: React.SFC<Props> = ({
  reviewSummaryBuckets,
  currentRatingFilter,
  onSetRatingFilter
}) => {
  if (!reviewSummaryBuckets) {
    throw new Error('Attempted to render review summary without rating facets')
  }

  const maybeIncompleteReviewBuckets = sortBy(reviewSummaryBuckets.buckets, [
    bucket => bucket.value
  ]).reverse()

  // Make sure we have a facet for every rating so there aren't missing star rows
  const defaultBucketNumbers = ['5', '4', '3', '2', '1']
  const reviewBuckets = defaultBucketNumbers.map((bucketValue, idx) => {
    const maybeReviewBucket = maybeIncompleteReviewBuckets.find(
      bucket => bucket.value === bucketValue
    )

    if (!maybeReviewBucket) {
      return {
        count: 0,
        value: defaultBucketNumbers[idx]
      }
    }

    return maybeReviewBucket
  })

  const totalReviews = reviewBuckets.reduce((sum, bucket) => sum + bucket.count, 0)

  const averageRating =
    reviewBuckets.reduce((sum, bucket) => sum + parseInt(bucket.value, 10) * bucket.count, 0) /
    totalReviews

  return (
    <div className={styles.stickyContainer}>
      <div className={styles.starRatingContainer}>
        <StarRating rating={averageRating} starDimension="26px" />
        <div className={styles.starRating}>
          <span>{Math.round(averageRating * 100) / 100}</span> / 5 stars
        </div>
      </div>
      <div className={styles.individualStarContainer}>
        {reviewBuckets.map(({ value, count }, index) => {
          const percentage = Math.round((count / totalReviews) * 100)
          return (
            <div
              key={index}
              className={styles.individualStar}
              onClick={() => {
                // If it's already selected, reset the filter
                // If we can filter by it (it has a count), update the active filter
                // If it doesn't have a count, it's not a real facet, do nothing
                if (value === currentRatingFilter) {
                  onSetRatingFilter()

                  scroller.scrollTo('feedHeader', {
                    smooth: true,
                    offset: -110
                  })
                } else if (count > 0) {
                  onSetRatingFilter(value)

                  scroller.scrollTo('feedHeader', {
                    smooth: true,
                    offset: -110
                  })
                }
              }}
            >
              <div className={styles.starCountText}>{value}</div>
              <Star
                className={classNames(styles.starCountIcon, {
                  [styles.clickable]: count > 0
                })}
              />
              <div
                className={classNames(styles.starBarContainer, {
                  [styles.unclickable]: count === 0,
                  [styles.selected]: value === currentRatingFilter,
                  [styles.unselected]: count > 0 && value !== currentRatingFilter
                })}
              >
                <div
                  className={classNames(styles.starBarFilled, {
                    [styles.selected]: value === currentRatingFilter
                  })}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div
                className={classNames(styles.starCountText, {
                  [styles.selected]: value === currentRatingFilter
                })}
              >
                {count} ({percentage}%)
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default Summary
